import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import videojs from 'video.js';
import Record from 'videojs-record/dist/videojs.record.js';
import { MediaRecordStatus } from 'library-explorer';

@Component({
    selector: 'app-video-record',
    templateUrl: './video-record.component.html',
    styleUrls: ['./video-record.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VideoRecordComponent implements AfterViewInit, OnInit, OnDestroy {
  @Output() public videoRecorded: EventEmitter<Blob> = new EventEmitter();
  @ViewChild('videoElement', { static: true }) public videoElement: ElementRef<HTMLVideoElement>;

  @Input() recordType: 'screen' | 'camera' = 'camera';
  @Input() public set maxVideoLength(value: number) {
    this._maxVideoLength = value || this.defaultMaxLength;
  }

  public get maxVideoLength() {
    return this._maxVideoLength;
  }

  @Input() public canUpload = true;

  @Input() public previewMode = false;

  private _maxVideoLength = 600;
  private readonly defaultMaxLength = 600;


  private config: any = {
    controls: false,
    autoplay: false,
    fluid: true,
    loop: false,
    bigPlayButton: false,
    controlBar: {
      volumePanel: false
    },
    plugins: {
      record: {
        audio: true,
        recordIndicator: false,
        deviceButton: false,
        debug: environment.production === false,
        timeSlice: 500
      }
    }
  };
  private player = null;
  private plugin = Record;

  public status: MediaRecordStatus = MediaRecordStatus.INIT;
  public statuses: typeof MediaRecordStatus = MediaRecordStatus;

  public timestamp = 0;
  public progress = 0;

  public recordData: Blob = null;
  public recordDataUrl: string;

  public deviceReady = false;
  public startInterval: ReturnType<typeof setInterval>;;

  public paused = false;

  constructor(
    private readonly translateService: TranslateService,
    private readonly toastr: ToastrService,
    private readonly elementRef: ElementRef) {
    this.player = null;
    this.plugin = Record;
  }

  ngOnInit() {
    if (this.previewMode) {
      return;
    }
  
    this.setConfigurations();
    this.checkBrowserSupport();
  }


  async ngAfterViewInit() {
    if (this.previewMode) {
      return;
    }
  
    if (this.recordType === 'camera') {
      await this.initVideoJs();
    }
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.dispose();
      this.player = false;
    }
  }

  public videoDurationInitialized(duration: number) {
    if (this.status === MediaRecordStatus.UPLOADED) {
      this.timestamp = duration;
      if (duration > this.maxVideoLength) {
        const error = this.translateService.instant('MANUAL_ASSIGNMENT.media_file_upload_error');
        this.toastr.error(error);

        this.resetPlayer();
      }
    }
  }

  public fileUploaded(event): void {
    const file: File = event.target.files && event.target.files[0];
    const videoRegex = /^video\//;
    const isVideo = file.type.match(videoRegex);

    if (isVideo) {
      this.recordData = file;
      this.recordDataUrl = URL.createObjectURL(this.recordData);
      this.videoRecorded.emit(this.recordData);

      this.status = MediaRecordStatus.UPLOADED;
    } else {
      const error = this.translateService.instant('ERRORS.invalid_file_extension', { type: 'video' });
      this.toastr.error(error);
    }
  }

  public resetPlayer(): void {
    this.player.record().reset();
    this.player.deviceButton.handleClick();
    this.status = MediaRecordStatus.INIT;
    this.recordData = null;
    this.recordDataUrl = null;
    this.timestamp = 0;
    this.videoRecorded.emit(null);
  }

  public async startRecording(): Promise<void> {
    if (this.paused) {
      this.player.record().resume();
      this.paused = false;

      return;
    }

    if (!this.player) {
      await this.initVideoJs();
    }

    this.status = MediaRecordStatus.RECORDING;
    this.timestamp = -3;
    this.startInterval = setInterval(() => {
      this.timestamp += 1;

      if (this.timestamp === 0) {
        this.player.record().start();
        clearInterval(this.startInterval);
        this.startInterval = null;
      }
    }, 1000);
  }

  public stopRecording(): void {
    if (this.paused) {
      this.startRecording();
      return;
    }

    if (this.startInterval) {
      clearInterval(this.startInterval);
      this.timestamp = 0;
      this.status = MediaRecordStatus.INIT;
    } else {
      const tracks = this.player.record()?.mediaElement?.srcObject?.getTracks() || [];
      tracks.forEach((track) => track.stop());
      this.player.record().stop();
    }
  }

  public pauseRecording(): void {
    this.paused = true;
    this.player.record().pause();
  }

  private setConfigurations(): void {
    this.config.plugins.record.maxLength = this.maxVideoLength;
    this.config.plugins.record.screen = this.recordType === 'screen',
      this.config.plugins.record.video = this.recordType === 'camera'
        ? {
          width: { min: 320, ideal: 960, max: 960 },
          height: { min: 280, ideal: 540, max: 540 }
        }
        : false;
  }

  private async initVideoJs(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.player = videojs(this.videoElement.nativeElement, this.config, () => {
        this.player.deviceButton.handleClick();
      });

      this.player.on('deviceReady', () => {
        if (this.recordType === 'screen' && !this.deviceReady) {
          // when 'Stop sharing' button is clicked in Chrome
          const stream = (this.videoElement.nativeElement as any).captureStream();
          stream.getVideoTracks()[0].addEventListener('ended', () => {
            this.stopRecording();
          });
        }

        this.deviceReady = true;
        resolve();
      });

      this.player.on('startRecord', () => {
        this.status = MediaRecordStatus.RECORDING;
      });

      this.player.on('finishRecord', () => {
        this.status = MediaRecordStatus.FINISHED;
        this.recordData =  this.player.recordedData;
        this.recordDataUrl = URL.createObjectURL(this.recordData);
        this.videoRecorded.emit(this.recordData);
      });

      this.player.on('error', (element, error) => {
        this.toastr.error(error);
      });

      this.player.on('deviceError', (err) => {
        if (this.player.deviceErrorCode) {
          this.toastr.error(this.player.deviceErrorCode);
        } else {
          const error = this.translateService.instant('COMMON.browser_not_support_recording');
          this.toastr.error(error);
        }
      });

      this.player.on('timestamp', (data) => {
        this.timestamp = this.player.record().getCurrentTime();
        this.progress = 100 * this.timestamp / this.maxVideoLength;
      });
    });
  }

  private checkBrowserSupport(): void {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      const error = this.translateService.instant('COMMON.browser_not_support_recording');
      this.toastr.error(error);
    }
  }
}
